import { useState, useEffect, useMemo, Fragment } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreVertical, Pencil, Trash2, UserCheck, UserX, Eye, KeyRound, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Building2, Mail, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format a raw phone string for readability as (XXX) XXX-XXXX. Strips a leading
// US country code (1) on 11-digit numbers. Anything unexpected is returned as-is.
const formatPhone = (raw) => {
  if (!raw) return "-";
  let d = String(raw).replace(/\D/g, "");
  if (d.length === 11 && d[0] === "1") d = d.slice(1);
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
};

const userCompanies = (u) =>
  (u?.company_names && u.company_names.length > 0)
    ? u.company_names
    : (u?.company_name ? [u.company_name] : []);

const UserManagement = () => {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [pendingTermsOnly, setPendingTermsOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [submitting, setSubmitting] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState(() => new Set());

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "customer",
    company_names: [],
    phone: "",
  });
  const [companyInput, setCompanyInput] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, [filterRole]);

  useEffect(() => {
    // Load all known company names (Pay Sheet + already-assigned to any user)
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/all-known-companies`);
        setAllCompanies(res.data?.companies || []);
      } catch (error) {
        console.error("Failed to load company list:", error);
      }
    })();
  }, []);

  const fetchUsers = async () => {
    try {
      const params = filterRole !== "all" ? { role: filterRole } : {};
      const response = await axios.get(`${API}/users`, { params });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      email: "",
      password: "",
      name: "",
      role: "customer",
      company_names: [],
      phone: "",
    });
    setCompanyInput("");
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    const names = (user.company_names && user.company_names.length > 0)
      ? user.company_names
      : (user.company_name ? [user.company_name] : []);
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: "",
      name: user.name,
      role: user.role,
      company_names: names,
      phone: user.phone || "",
    });
    setCompanyInput("");
    setDialogOpen(true);
  };

  const addCompany = (nameArg, { allowNew = false } = {}) => {
    const v = (nameArg ?? companyInput).trim();
    if (!v) return;
    if (formData.company_names.some(n => n.toLowerCase() === v.toLowerCase())) {
      setCompanyInput("");
      setCompanyDropdownOpen(false);
      return;
    }
    const match = allCompanies.find(n => n.toLowerCase() === v.toLowerCase());
    if (!match && !allowNew) {
      toast.error(`"${v}" is not a valid company. Pick one from the list or click Add new.`);
      return;
    }
    const finalName = match || v;
    setFormData(prev => ({ ...prev, company_names: [...prev.company_names, finalName] }));
    if (!match) {
      // Add to local options so it stays in the dropdown for the rest of the session
      setAllCompanies(prev => [...prev, finalName].sort((a, b) => a.localeCompare(b)));
    }
    setCompanyInput("");
    setCompanyDropdownOpen(false);
  };

  const removeCompany = (name) => {
    setFormData(prev => ({ ...prev, company_names: prev.company_names.filter(n => n !== name) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.password;
        delete updateData.email;
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success("User updated successfully");
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success("User created successfully");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Operation failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const userData = await impersonate(user.id);
      toast.success(`Now viewing as ${userData.name}`);
      navigate(`/${userData.role}`);
    } catch (error) {
      toast.error("Failed to impersonate user");
    }
  };

  const openPasswordDialog = (user) => {
    setResetPasswordUser(user);
    setNewPassword("");
    setPasswordDialogOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/users/reset-password`, {
        user_id: resetPasswordUser.id,
        new_password: newPassword,
      });
      toast.success(`Password reset for ${resetPasswordUser.name}`);
      setPasswordDialogOpen(false);
      setNewPassword("");
    } catch (error) {
      toast.error("Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const response = await axios.post(`${API}/users/${user.id}/toggle-active`);
      const newStatus = response.data.is_active ? "activated" : "deactivated";
      toast.success(`${user.name} has been ${newStatus}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleResendWelcome = async (user) => {
    try {
      await axios.post(`${API}/users/${user.id}/resend-welcome`);
      toast.success(`Welcome email sent to ${user.email}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send the welcome email";
      toast.error(message);
    }
  };

  // Sort and filter users
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} className="ml-1 text-muted-foreground/50" />;
    }
    return sortConfig.direction === "asc" 
      ? <ArrowUp size={14} className="ml-1 text-primary" />
      : <ArrowDown size={14} className="ml-1 text-primary" />;
  };

  // Terms acceptance is per COMPANY: if any customer in a company has accepted, every user
  // in that company counts as accepted (additional users don't need to accept again).
  const companyAcceptance = useMemo(() => {
    const map = new Map(); // lowercased company -> { at, name }
    for (const u of users) {
      if (u.role === "customer" && u.terms_accepted === true) {
        for (const c of userCompanies(u)) {
          const k = c.toLowerCase();
          if (!map.has(k)) map.set(k, { at: u.terms_accepted_at, name: u.terms_accepted_name });
        }
      }
    }
    return map;
  }, [users]);

  const companyAcceptanceFor = (u) => {
    for (const c of userCompanies(u)) {
      const info = companyAcceptance.get(c.toLowerCase());
      if (info) return info;
    }
    return null;
  };
  const isUserAccepted = (u) => u.terms_accepted === true || !!companyAcceptanceFor(u);
  const isTermsPending = (u) => u.role === "customer" && !isUserAccepted(u);

  // Build display units: customers are grouped by company (earliest-created = primary),
  // admins/callers stay as individual rows.
  const displayUnits = useMemo(() => {
    const groups = new Map(); // lowercased-company -> { displayName, users }
    const singles = [];

    for (const u of users) {
      if (u.role !== "customer") {
        singles.push(u);
        continue;
      }
      const companies = (u.company_names && u.company_names.length > 0)
        ? u.company_names
        : (u.company_name ? [u.company_name] : []);
      if (companies.length === 0) {
        singles.push(u);
        continue;
      }
      for (const c of companies) {
        const key = c.toLowerCase();
        if (!groups.has(key)) groups.set(key, { displayName: c, users: [] });
        groups.get(key).users.push(u);
      }
    }

    const units = [];
    for (const { displayName, users: groupUsers } of groups.values()) {
      const sorted = [...groupUsers].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      const primary = sorted[0];
      const secondaries = sorted.slice(1);
      const latestLogin = sorted.reduce((acc, u) => (u.last_login && (!acc || u.last_login > acc)) ? u.last_login : acc, null);
      const anyActive = sorted.some(u => u.is_active);
      units.push({
        type: "group",
        key: `group::${displayName.toLowerCase()}`,
        companyName: displayName,
        primary,
        secondaries,
        latestLogin,
        anyActive,
      });
    }
    for (const u of singles) {
      units.push({ type: "single", key: `single::${u.id}`, user: u });
    }

    // Role filter
    let filtered = units;
    if (filterRole !== "all") {
      filtered = filtered.filter(u => {
        if (u.type === "single") return u.user.role === filterRole;
        // Groups are always customers; only show if customer role is selected (or all)
        return filterRole === "customer";
      });
    }

    // Pending Terms & Conditions filter (new customers who haven't accepted yet)
    if (pendingTermsOnly) {
      filtered = filtered.filter(u => {
        if (u.type === "single") return isTermsPending(u.user);
        return isTermsPending(u.primary) || u.secondaries.some(isTermsPending);
      });
    }

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchUser = (user) => {
        const cs = (user.company_names && user.company_names.length > 0
          ? user.company_names.join(", ")
          : (user.company_name || "")
        ).toLowerCase();
        return (
          user.name?.toLowerCase().includes(q) ||
          user.email?.toLowerCase().includes(q) ||
          cs.includes(q) ||
          user.role?.toLowerCase().includes(q)
        );
      };
      filtered = filtered.filter(u => {
        if (u.type === "single") return matchUser(u.user);
        return u.companyName.toLowerCase().includes(q)
          || matchUser(u.primary)
          || u.secondaries.some(matchUser);
      });
    }

    // Sort: derive the comparable value from either single user or group primary/aggregate
    const valueOf = (u, key) => {
      if (u.type === "single") {
        if (key === "is_active") return u.user.is_active ? 1 : 0;
        if (key === "last_login") return u.user.last_login || "";
        return (u.user[key] || "").toString().toLowerCase();
      }
      if (key === "is_active") return u.anyActive ? 1 : 0;
      if (key === "last_login") return u.latestLogin || "";
      return (u.primary[key] || "").toString().toLowerCase();
    };
    const sorted = [...filtered].sort((a, b) => {
      const av = valueOf(a, sortConfig.key);
      const bv = valueOf(b, sortConfig.key);
      if (sortConfig.key === "is_active") {
        return sortConfig.direction === "asc" ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [users, searchQuery, filterRole, pendingTermsOnly, sortConfig, companyAcceptance]);

  // When searching, auto-expand any group whose match is in a secondary user
  const effectivelyExpanded = useMemo(() => {
    if (!searchQuery && !pendingTermsOnly) return expandedCompanies;
    const q = searchQuery.toLowerCase();
    const expanded = new Set(expandedCompanies);
    for (const u of displayUnits) {
      if (u.type !== "group") continue;
      if (searchQuery) {
        const primaryMatch = (u.primary.name?.toLowerCase().includes(q) || u.primary.email?.toLowerCase().includes(q));
        const secondaryMatch = u.secondaries.some(s => s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q));
        if (secondaryMatch && !primaryMatch) expanded.add(u.companyName);
      }
      // Surface a pending secondary so it's visible when filtering by pending terms.
      if (pendingTermsOnly && u.secondaries.some(isTermsPending)) expanded.add(u.companyName);
    }
    return expanded;
  }, [displayUnits, searchQuery, pendingTermsOnly, expandedCompanies, companyAcceptance]);

  const toggleCompany = (companyName) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyName)) next.delete(companyName);
      else next.add(companyName);
      return next;
    });
  };

  const totalVisibleUsers = useMemo(() => {
    return displayUnits.reduce((sum, u) => sum + (u.type === "single" ? 1 : 1 + u.secondaries.length), 0);
  }, [displayUnits]);

  // Renders a single TableRow for a user. Used for: standalone (admins/callers), group primary, and group secondaries.
  // opts:
  //   isGroupRow=true → render the chevron + company name + secondaries count in the Name column
  //   indent=true     → secondary user under an expanded group; muted styling
  //   latestLogin     → for group-primary, show this aggregate instead of the primary's own last_login
  //   anyActive       → for group-primary, show Active if any user in the company is active
  const renderUserRow = (user, opts = {}) => {
    const { isGroupRow = false, indent = false, groupCompanyName, secondariesCount = 0, latestLogin, anyActive, isExpanded, onToggle } = opts;
    const lastLoginVal = isGroupRow ? latestLogin : user.last_login;
    const isActiveVal = isGroupRow ? anyActive : user.is_active;
    return (
      <TableRow
        key={isGroupRow ? `${groupCompanyName}-primary-${user.id}` : `${groupCompanyName || "single"}-${user.id}`}
        className={`hover:bg-[#0d2a5a] border-b border-[#1a3a6a] ${indent ? "bg-[#0a1f3f]" : ""}`}
      >
        <TableCell className="font-medium text-xs sm:text-sm py-2 sm:py-4 text-white">
          {isGroupRow ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onToggle}
                aria-label={isExpanded ? "Collapse" : "Expand"}
                className="text-white/60 hover:text-white"
              >
                {secondariesCount > 0 ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="inline-block w-[14px]" />}
              </button>
              <Building2 size={14} className="text-[#f4cc1e]/70" />
              <span className="font-semibold">{groupCompanyName}</span>
              {secondariesCount > 0 && (
                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#1a3a6a] text-white/70">
                  +{secondariesCount} user{secondariesCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
          ) : indent ? (
            <span className="pl-7 text-white/80">{user.name}{user.role === "admin" && <Star size={12} className="text-[#f4cc1e] ml-1 inline align-baseline" fill="currentColor" />}</span>
          ) : (
            <span>{user.name}{user.role === "admin" && <Star size={12} className="text-[#f4cc1e] ml-1 inline align-baseline" fill="currentColor" />}</span>
          )}
        </TableCell>
        <TableCell className="font-mono text-xs hidden sm:table-cell text-white/70">{user.email}</TableCell>
        <TableCell className="text-xs sm:text-sm hidden md:table-cell text-white/80">{user.sheet_contact || "-"}</TableCell>
        <TableCell className="font-mono text-xs hidden sm:table-cell text-white/70">{formatPhone(user.sheet_phone)}</TableCell>
        <TableCell className="hidden md:table-cell">
          {(() => {
            if (user.role !== "customer") return <span className="text-white/30 text-xs">—</span>;
            const info = user.terms_accepted
              ? { at: user.terms_accepted_at, name: user.terms_accepted_name, self: true }
              : companyAcceptanceFor(user);
            if (info) {
              const acceptedBy = !info.self && info.name ? ` by ${info.name}` : "";
              return (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-green-600/20 text-green-400"
                  title={`Accepted${info.at ? " " + new Date(info.at).toLocaleString() : ""}${acceptedBy}`}
                >
                  <UserCheck size={12} />
                  {info.at
                    ? new Date(info.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : "Accepted"}
                </span>
              );
            }
            return (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-amber-500/20 text-amber-400 font-medium">
                Pending
              </span>
            );
          })()}
        </TableCell>
        <TableCell className="hidden sm:table-cell">
          {isActiveVal ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-green-600/20 text-green-400">
              <UserCheck size={12} />
              Active
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 text-xs rounded-sm bg-[#1a3a6a] text-white/50">
              Inactive
            </span>
          )}
        </TableCell>
        <TableCell className="text-xs hidden lg:table-cell text-white/70 font-mono">
          {lastLoginVal ? new Date(lastLoginVal).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : <span className="text-white/30">Never</span>}
        </TableCell>
        <TableCell className="text-xs hidden lg:table-cell text-white/70 font-mono">
          {user.intro_video_completed_at ? (
            new Date(user.intro_video_completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          ) : user.intro_video_required ? (
            <span className="text-amber-400/80">Not watched</span>
          ) : (
            <span className="text-white/30">Not required</span>
          )}
        </TableCell>
        <TableCell className="text-right py-2 sm:py-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-[#1a3a6a]" data-testid={`user-actions-${user.id}`}>
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#091c3c] border-[#1a3a6a]">
              <DropdownMenuItem
                onClick={() => openEditDialog(user)}
                className="cursor-pointer text-white hover:bg-[#1a3a6a]"
              >
                <Pencil size={14} className="mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleImpersonate(user)}
                className="cursor-pointer text-white hover:bg-[#1a3a6a]"
                data-testid={`impersonate-${user.id}`}
              >
                <Eye size={14} className="mr-2" />
                View As
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openPasswordDialog(user)}
                className="cursor-pointer text-white hover:bg-[#1a3a6a]"
                data-testid={`reset-password-${user.id}`}
              >
                <KeyRound size={14} className="mr-2" />
                Reset Password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleResendWelcome(user)}
                className="cursor-pointer text-white hover:bg-[#1a3a6a]"
                data-testid={`resend-welcome-${user.id}`}
              >
                <Mail size={14} className="mr-2" />
                Resend Welcome Email
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleToggleActive(user)}
                className={`cursor-pointer ${user.is_active ? 'text-orange-400 hover:bg-orange-500/20' : 'text-green-400 hover:bg-green-500/20'}`}
                data-testid={`toggle-active-${user.id}`}
              >
                {user.is_active ? (
                  <>
                    <UserX size={14} className="mr-2" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck size={14} className="mr-2" />
                    Activate
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDelete(user.id)}
                className="cursor-pointer text-red-400 hover:bg-red-500/20"
              >
                <Trash2 size={14} className="mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-full bg-[#f4cc1e] p-4 sm:p-6 rounded-xl min-h-screen" data-testid="user-management">
      {/* Header */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white font-['Chivo']">User Management</h1>
              <p className="text-sm text-white/60">Manage admins, callers, and customers</p>
            </div>
            <Button
              onClick={openCreateDialog}
              data-testid="create-user-btn"
              className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold uppercase tracking-wide rounded-xl w-full sm:w-auto"
            >
              <Plus size={18} className="mr-2" />
              Add User
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="relative w-full">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0d2a5a] border-[#1a3a6a] text-white w-full"
                data-testid="search-users"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-full sm:w-48 bg-[#0d2a5a] border-[#1a3a6a] text-white" data-testid="role-filter">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                    <SelectItem value="all" className="text-white">All Roles</SelectItem>
                    <SelectItem value="admin" className="text-white">Admin</SelectItem>
                    <SelectItem value="caller" className="text-white">Caller</SelectItem>
                    <SelectItem value="customer" className="text-white">Customer</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => setPendingTermsOnly((v) => !v)}
                  className={`h-10 px-3 rounded-md border text-sm whitespace-nowrap transition-colors ${
                    pendingTermsOnly
                      ? "bg-amber-500/20 border-amber-500/50 text-amber-300 font-medium"
                      : "bg-[#0d2a5a] border-[#1a3a6a] text-white/70 hover:border-amber-500/40"
                  }`}
                  data-testid="pending-terms-filter"
                >
                  Pending T&amp;C only
                </button>
              </div>
              <div className="text-sm text-white/60">
                Showing {totalVisibleUsers} of {users.length} users
                {searchQuery && <span className="ml-1">matching "{searchQuery}"</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg w-full max-w-full overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto w-full">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b border-[#1a3a6a]">
                  <TableHead 
                    className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold cursor-pointer hover:bg-[#1a3a6a] transition-colors whitespace-nowrap"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center">
                      Name {getSortIcon("name")}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold cursor-pointer hover:bg-[#1a3a6a] transition-colors whitespace-nowrap hidden sm:table-cell"
                    onClick={() => handleSort("email")}
                  >
                    <div className="flex items-center">
                      Email {getSortIcon("email")}
                    </div>
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold whitespace-nowrap hidden md:table-cell">
                    Contact
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold whitespace-nowrap hidden sm:table-cell">
                    Phone
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold whitespace-nowrap hidden md:table-cell">
                    Terms
                  </TableHead>
                  <TableHead
                    className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold cursor-pointer hover:bg-[#1a3a6a] transition-colors whitespace-nowrap hidden sm:table-cell"
                    onClick={() => handleSort("is_active")}
                  >
                    <div className="flex items-center">
                      Status {getSortIcon("is_active")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold cursor-pointer hover:bg-[#1a3a6a] transition-colors whitespace-nowrap hidden lg:table-cell"
                    onClick={() => handleSort("last_login")}
                  >
                    <div className="flex items-center">
                      Last Login {getSortIcon("last_login")}
                    </div>
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold whitespace-nowrap hidden lg:table-cell">
                    Welcome Video
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold text-right whitespace-nowrap">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayUnits.map((unit) => {
                  if (unit.type === "single") {
                    return renderUserRow(unit.user, { isGroupRow: false, indent: false });
                  }
                  const isExpanded = effectivelyExpanded.has(unit.companyName);
                  return (
                    <Fragment key={unit.key}>
                      {renderUserRow(unit.primary, {
                        isGroupRow: true,
                        indent: false,
                        groupCompanyName: unit.companyName,
                        secondariesCount: unit.secondaries.length,
                        latestLogin: unit.latestLogin,
                        anyActive: unit.anyActive,
                        isExpanded,
                        onToggle: () => toggleCompany(unit.companyName),
                      })}
                      {isExpanded && unit.secondaries.map(secondary => renderUserRow(secondary, { isGroupRow: false, indent: true }))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white">
              {editingUser ? "Edit User" : "Create New User"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white/80">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingUser}
                  data-testid="user-email-input"
                  className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
                />
              </div>
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  data-testid="user-password-input"
                  className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role" className="text-white/80">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => handleSelectChange("role", value)}
                >
                  <SelectTrigger data-testid="user-role-select" className="bg-[#0d2a5a] border-[#1a3a6a] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                    <SelectItem value="admin" className="text-white">Admin</SelectItem>
                    <SelectItem value="caller" className="text-white">Caller</SelectItem>
                    <SelectItem value="customer" className="text-white">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/80">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  data-testid="user-phone-input"
                  className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_input" className="text-white/80">Companies</Label>
              {formData.company_names.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-2 bg-[#0d2a5a] border border-[#1a3a6a] rounded-md">
                  {formData.company_names.map((name, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#f4cc1e]/20 text-[#f4cc1e] text-xs font-medium">
                      {name}
                      <button
                        type="button"
                        onClick={() => removeCompany(name)}
                        className="hover:text-red-400"
                        aria-label={`Remove ${name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <Input
                  id="company_input"
                  value={companyInput}
                  onChange={(e) => { setCompanyInput(e.target.value); setCompanyDropdownOpen(true); }}
                  onFocus={() => setCompanyDropdownOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const raw = companyInput.trim();
                      const q = raw.toLowerCase();
                      if (!raw) return;
                      const alreadyAssigned = formData.company_names.some(x => x.toLowerCase() === q);
                      if (alreadyAssigned) return;
                      const filtered = allCompanies
                        .filter(n => !formData.company_names.some(x => x.toLowerCase() === n.toLowerCase()))
                        .filter(n => n.toLowerCase().includes(q));
                      if (filtered.length > 0) {
                        addCompany(filtered[0]);
                      } else {
                        addCompany(raw, { allowNew: true });
                      }
                    } else if (e.key === "Escape") {
                      setCompanyDropdownOpen(false);
                    }
                  }}
                  placeholder="Search for a company..."
                  data-testid="user-company-input"
                  className="bg-[#0d2a5a] border-[#1a3a6a] text-white pr-8"
                  autoComplete="off"
                />
                {companyInput && (
                  <button
                    type="button"
                    onClick={() => { setCompanyInput(""); setCompanyDropdownOpen(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    aria-label="Clear"
                  >
                    ×
                  </button>
                )}
                {companyDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setCompanyDropdownOpen(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-[#091c3c] border border-[#1a3a6a] rounded-md shadow-xl max-h-56 overflow-y-auto">
                      {(() => {
                        const raw = companyInput.trim();
                        const q = raw.toLowerCase();
                        const filtered = allCompanies
                          .filter(n => !formData.company_names.some(x => x.toLowerCase() === n.toLowerCase()))
                          .filter(n => !q || n.toLowerCase().includes(q));
                        const exactMatch = !!allCompanies.find(n => n.toLowerCase() === q);
                        const alreadyAssigned = formData.company_names.some(x => x.toLowerCase() === q);
                        const showAddNew = raw && !exactMatch && !alreadyAssigned;
                        return (
                          <>
                            {filtered.length === 0 && !showAddNew && (
                              <div className="px-3 py-2 text-sm text-white/50 italic">No matching companies</div>
                            )}
                            {filtered.slice(0, 100).map((name) => (
                              <div
                                key={name}
                                className="px-3 py-2 text-sm text-white/80 hover:bg-[#0d2a5a] cursor-pointer"
                                onClick={() => addCompany(name)}
                              >
                                {name}
                              </div>
                            ))}
                            {showAddNew && (
                              <>
                                {filtered.length > 0 && <div className="border-t border-[#1a3a6a]" />}
                                <div
                                  className="px-3 py-2 text-sm text-[#f4cc1e] hover:bg-[#0d2a5a] cursor-pointer flex items-center gap-1.5"
                                  onClick={() => addCompany(raw, { allowNew: true })}
                                >
                                  <span className="text-base leading-none">+</span>
                                  Add "{raw}" as a new company
                                </div>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-white/40">Pick from existing companies or type a new one and click Add new — useful for onboarding customers before they appear in the pay sheet. The first company in the list is the default active company.</p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="save-user-btn"
                className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
              >
                {submitting ? "Saving..." : editingUser ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white">
              Reset Password for {resetPasswordUser?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password" className="text-white/80">New Password *</Label>
              <Input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
                data-testid="new-password-input"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPasswordDialogOpen(false)}
                className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="reset-password-btn"
                className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
              >
                {submitting ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
